import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { acsAPI, customerAPI } from '../services/api';
import toast from 'react-hot-toast';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import {
  Router, Wifi, WifiOff, Search, RefreshCw, RotateCcw, Power,
  AlertTriangle, X, Radio, Users, Activity, Trash2, Key, Copy, Settings, List,
  ChevronDown, ChevronUp, Globe, Link2, Edit2, ShieldOff, ShieldCheck, Ban, Gauge,
} from 'lucide-react';
import useDarkReady from '../theme/useDarkReady';

const SPEEDTEST_SIZE = 50 * 1024 * 1024; // 50MB, both directions
const sleep = (ms) => new Promise(r => setTimeout(r, ms));

// Per-vendor TR-143 reliability notes — confirmed live 2026-07-26 against real units
// of each model. These are firmware behaviors on the ONT side, not bugs in this app;
// surfaced so staff don't mistake an odd/failed reading here for our tooling being broken.
const SPEEDTEST_VENDOR_NOTES = {
  skyworth: 'Skyworth ONTs: Upload usually never completes (firmware doesn’t run it) — if it hangs, that’s the router, not this test. Download may pull well over the requested 50MB.',
  tplink: 'TP-Link ONTs typically report slightly less data than requested — the Mbps figure is still accurate.',
};

const timeAgo = (d) => {
  if (!d) return '—';
  const secs = Math.max(0, Math.floor((Date.now() - new Date(d).getTime()) / 1000));
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
};

const fmtDuration = (secs) => {
  if (secs == null) return '—';
  const d = Math.floor(secs / 86400), h = Math.floor((secs % 86400) / 3600), m = Math.floor((secs % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

// ─── Connection Info Panel ────────────────────────────────────────────────────
function DetailField({ label, value, copyable }) {
  const copy = () => { navigator.clipboard?.writeText(value); toast.success('Copied to clipboard'); };
  return (
    <div>
      <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 4 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span className={copyable ? 'font-mono' : undefined} style={{ fontSize: copyable ? '.85rem' : '.9rem', fontWeight: 600, wordBreak: 'break-all' }}>{value}</span>
        {copyable && (
          <button className="btn btn-secondary btn-sm" style={{ padding: '3px 6px', flexShrink: 0 }} title="Copy" onClick={copy}>
            <Copy size={11} />
          </button>
        )}
      </div>
    </div>
  );
}

function ConnectionInfoPanel({ info }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="card" style={{ marginBottom: 24 }}>
      <div
        className="card-header"
        style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setOpen(o => !o)}
      >
        <Settings size={15} color="var(--primary)" />
        <h3 style={{ margin: 0, fontSize: '.95rem', flex: 1 }}>ACS Connection Details</h3>
        {open ? <ChevronUp size={16} color="var(--text-muted)" /> : <ChevronDown size={16} color="var(--text-muted)" />}
      </div>
      {open && (
        <div className="card-body" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <DetailField label={'ONT / TR-069 "ACS URL"'} value={info.cwmpUrl} copyable />
            <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 4 }}>
              Set this as the ACS URL / management server address on each ONT so it registers here.
            </div>
          </div>
          {/* Alternate "if that one doesn't work, try these" ACS URLs used to sit here.
              Both pointed at ports that were never reachable from the field, so the menu
              only ever offered a technician two ways to misconfigure an ONT. One URL. */}
          <DetailField label="CWMP Port" value={info.cwmpPort} />
          <DetailField label="Public Host" value={info.publicHost || '—'} />
          <DetailField label="Periodic Inform Interval" value={`${info.informInterval}s`} />
          <div>
            <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 4 }}>ONT Auth</div>
            <div style={{ fontWeight: 600, fontSize: '.9rem' }}>
              {info.authRequired ? 'Basic Auth (required)' : <span style={{ color: 'var(--text-muted)' }}>Not required</span>}
            </div>
          </div>
          {info.authRequired && (
            <>
              <DetailField label="ACS Username" value={info.cwmpUsername} copyable />
              <DetailField label="ACS Password" value={info.cwmpPassword} copyable />
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── WiFi Password Modal ──────────────────────────────────────────────────────
function WifiModal({ serial, wifi, onClose, onSuccess }) {
  const [band, setBand] = useState(wifi?.[0]?.band === '5' ? '5' : '2.4');
  const [ssid, setSsid] = useState('');
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!ssid && !password) { toast.error('Enter a new SSID or password'); return; }
    if (password && (password.length < 8 || password.length > 63)) {
      toast.error('WiFi password must be 8-63 characters'); return;
    }
    setSaving(true);
    try {
      const { data } = await acsAPI.wifi(serial, { band, ssid: ssid || undefined, password: password || undefined });
      toast.success(data.data?.completed ? 'WiFi updated' : 'Change queued — applies when the ONT next connects');
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'WiFi change failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
        <div className="modal-header">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Key size={16} color="var(--primary)" /> Change WiFi</h3>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Band</label>
              <select className="form-control" value={band} onChange={e => setBand(e.target.value)}>
                <option value="2.4">2.4 GHz</option>
                <option value="5">5 GHz</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">New SSID <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
              <input className="form-control" value={ssid} onChange={e => setSsid(e.target.value)} placeholder="Leave blank to keep current" />
            </div>
            <div className="form-group">
              <label className="form-label">New Password <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional, 8-63 chars)</span></label>
              <input className="form-control" value={password} onChange={e => setPassword(e.target.value)} placeholder="Leave blank to keep current" />
            </div>
          </div>
          <div style={{ padding: '0 24px 20px', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Applying…' : 'Apply'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── WAN / PPPoE Modal ─────────────────────────────────────────────────────────
function WanModal({ serial, wan, onClose, onSuccess }) {
  const primary = (wan || [])[0];
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [vlanId, setVlanId] = useState('');
  const [rebootToApply, setRebootToApply] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username && !password && !vlanId) { toast.error('Enter a new WAN username, password, or VLAN ID'); return; }
    if (vlanId && (!/^\d+$/.test(vlanId) || +vlanId < 1 || +vlanId > 4094)) {
      toast.error('VLAN ID must be a number between 1 and 4094'); return;
    }
    setSaving(true);
    try {
      const { data } = await acsAPI.wan(serial, {
        username: username || undefined,
        password: password || undefined,
        vlanId: vlanId || undefined,
        rebootToApply: vlanId ? rebootToApply : undefined,
      });
      toast.success(data.data?.completed ? 'WAN details updated' : 'Change queued — applies when the ONT next connects');
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'WAN change failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
        <div className="modal-header">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Globe size={16} color="var(--primary)" /> Change WAN / Internet</h3>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {primary && (
              <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', marginBottom: 14 }}>
                Current: <strong>{primary.username || '—'}</strong> {primary.connectionStatus ? `· ${primary.connectionStatus}` : ''}{primary.vlan ? ` · VLAN ${primary.vlan}` : ''}
              </div>
            )}
            <div className="form-group">
              <label className="form-label">PPPoE Username <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
              <input className="form-control" value={username} onChange={e => setUsername(e.target.value)} placeholder="Leave blank to keep current" />
            </div>
            <div className="form-group">
              <label className="form-label">PPPoE Password <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
              <input className="form-control" value={password} onChange={e => setPassword(e.target.value)} placeholder="Leave blank to keep current" />
            </div>
            <div className="form-group">
              <label className="form-label">VLAN ID <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional, 1-4094)</span></label>
              <input
                className="form-control"
                type="number"
                min="1"
                max="4094"
                value={vlanId}
                onChange={e => setVlanId(e.target.value)}
                placeholder="Leave blank to keep current"
              />
            </div>
            {vlanId && (
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: '.8rem', color: 'var(--text-muted)', cursor: 'pointer' }}>
                <input type="checkbox" checked={rebootToApply} onChange={e => setRebootToApply(e.target.checked)} style={{ marginTop: 2 }} />
                <span>
                  <strong style={{ color: 'var(--text)' }}>Reboot router to apply VLAN</strong><br />
                  Some routers accept the new VLAN but keep running on the old one until restarted.
                  Warning: if the new VLAN is wrong, the router will lose internet AND its connection
                  to this panel until fixed locally.
                </span>
              </label>
            )}
          </div>
          <div style={{ padding: '0 24px 20px', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Applying…' : 'Apply'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Customer Link Box ─────────────────────────────────────────────────────────
// Shown when an ONT isn't yet linked to an ERP customer, or (with currentCustomer
// set + onCancel provided) when relinking an already-linked ONT to a different
// customer — e.g. after an ONT swap or a wrong initial link. Tries an automatic
// match against the PPPoE username reported by the ONT's WAN connection; falls
// back to a manual customer search if that username isn't found (or unreported).
function CustomerLinkBox({ serial, pppoeUsername, currentCustomer, onLinked, onCancel }) {
  const [busy, setBusy] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const autoLink = async () => {
    setBusy(true);
    try {
      const { data } = await acsAPI.autoLinkCustomer(serial);
      toast.success(`Linked to ${data.data.customer.name} (${data.data.customer.customerId})`);
      onLinked();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Auto-link failed');
      setShowManual(true);
    } finally { setBusy(false); }
  };

  const runSearch = async (q) => {
    setQuery(q);
    if (q.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const found = await customerAPI.search(q, 8);
      setResults(found);
    } catch { /* ignore */ } finally { setSearching(false); }
  };

  const linkTo = async (customer) => {
    setBusy(true);
    try {
      await acsAPI.linkCustomer(serial, customer._id);
      toast.success(`Linked to ${customer.name} (${customer.customerId})`);
      onLinked();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Link failed');
    } finally { setBusy(false); }
  };

  return (
    <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ color: 'var(--text-muted)' }}>
          {currentCustomer
            ? <>Choose a new customer to relink from <strong>{currentCustomer.name}</strong>{pppoeUsername ? <> — WAN username <strong>{pppoeUsername}</strong></> : ''}</>
            : <>Not linked{pppoeUsername ? <> — WAN username <strong>{pppoeUsername}</strong></> : ''}</>}
        </span>
        {pppoeUsername && !showManual && (
          <button className="btn btn-secondary btn-sm" disabled={busy} onClick={autoLink}>
            <Link2 size={12} style={{ marginRight: 5 }} />{busy ? 'Linking…' : 'Link by WAN username'}
          </button>
        )}
        {!showManual && (
          <button className="btn btn-secondary btn-sm" onClick={() => setShowManual(true)}>Link manually</button>
        )}
        {onCancel && (
          <button className="btn btn-secondary btn-sm" disabled={busy} onClick={onCancel}>Cancel</button>
        )}
      </div>
      {showManual && (
        <div style={{ marginTop: 8, position: 'relative', maxWidth: 320 }}>
          <input
            className="form-control"
            style={{ height: 32, fontSize: '.82rem' }}
            placeholder="Search customer by name / mobile / billing no…"
            value={query}
            onChange={e => runSearch(e.target.value)}
            disabled={busy}
          />
          {(searching || results.length > 0) && query.trim().length >= 2 && (
            <div style={{ marginTop: 4, border: '1px solid var(--border)', borderRadius: 6, maxHeight: 180, overflowY: 'auto', background: 'var(--card-bg, #fff)' }}>
              {searching ? (
                <div style={{ padding: 8, fontSize: '.78rem', color: 'var(--text-muted)' }}>Searching…</div>
              ) : results.length === 0 ? (
                <div style={{ padding: 8, fontSize: '.78rem', color: 'var(--text-muted)' }}>No matches</div>
              ) : results.map(c => (
                <div key={c._id} onClick={() => !busy && linkTo(c)} style={{ padding: '6px 10px', fontSize: '.8rem', cursor: 'pointer', borderBottom: '1px solid var(--border)' }}>
                  {c.name} <span style={{ color: 'var(--text-muted)' }}>({c.customerId})</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Device Detail Modal ──────────────────────────────────────────────────────
export function DeviceDetailModal({ serial, onClose, onChanged, initialRelink }) {
  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // action name in flight
  const [showWifi, setShowWifi] = useState(false);
  const [showWan, setShowWan] = useState(false);
  const [rawParams, setRawParams] = useState(null);
  const [rawLoading, setRawLoading] = useState(false);
  const [relinking, setRelinking] = useState(!!initialRelink);
  const [unlinking, setUnlinking] = useState(false);
  const [aclInfo, setAclInfo] = useState(null); // null = loading; {supported:false,reason} or {supported:true,networks}
  const [aclBusy, setAclBusy] = useState(null); // MAC currently being blocked/unblocked
  const [speedtest, setSpeedtest] = useState(null); // {download:{state,bytes,mbps}, upload:{...}}
  const [stRunning, setStRunning] = useState(null); // 'download' | 'upload' | null
  const [liveMbps, setLiveMbps] = useState(null);
  const [liveHistory, setLiveHistory] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await acsAPI.getDevice(serial);
      setDevice(data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load ONT');
    } finally { setLoading(false); }
  }, [serial]);

  const loadAcl = useCallback(async () => {
    try {
      const { data } = await acsAPI.getMacAcl(serial);
      setAclInfo(data.data);
    } catch (err) {
      setAclInfo({ supported: false, reason: err.response?.data?.message || 'Failed to load' });
    }
  }, [serial]);

  const loadSpeedtest = useCallback(async () => {
    try {
      const { data } = await acsAPI.getSpeedtestResults(serial);
      setSpeedtest(data.data);
    } catch { /* non-critical, leave stale/empty */ }
  }, [serial]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadAcl(); }, [loadAcl]);
  useEffect(() => { loadSpeedtest(); }, [loadSpeedtest]);

  // TR-143 diagnostics run on the ONT itself against the ACS. The trigger POST doesn't
  // resolve until the CWMP round-trip with the ONT finishes, which can itself take
  // several seconds — and for a fast line a 50MB transfer can complete inside that same
  // window. So we must NOT await the trigger before polling: fire it and start polling
  // speedtest-progress (raw byte counter, updated live as the ONT pulls/pushes data)
  // immediately and concurrently, every ~400ms, to actually catch the transfer while
  // it's in flight. speedtest-results tells us when the ONT reports DiagnosticsState has
  // moved off Requested/None (final BOM/EOM-based mbps), or we give up (~40s/direction).
  //
  // Devices that are slow to pick up the connection request (or whose SPV round-trip
  // just takes a beat) haven't touched DiagnosticsState yet by our first poll — it's
  // still sitting on whatever the *previous* test left there, often already
  // "Completed". Treating that as "done" would show last run's stale numbers as if they
  // were fresh. So we snapshot the pre-trigger result and only accept a terminal state
  // once the polled result actually differs from that snapshot.
  const runOneDirection = async (direction) => {
    setStRunning(direction);
    setLiveMbps(null);
    setLiveHistory([]);
    const prevFingerprint = JSON.stringify(speedtest?.[direction] || null);
    let triggerError = null;
    (direction === 'download' ? acsAPI.speedtestDownload(serial, SPEEDTEST_SIZE) : acsAPI.speedtestUpload(serial, SPEEDTEST_SIZE))
      .catch(err => { triggerError = err; });
    let lastDone = 0;
    let lastTs = Date.now();
    for (let i = 0; i < 100; i++) {
      await sleep(400);
      if (triggerError) throw triggerError;
      try {
        const { data: prog } = await acsAPI.getSpeedtestProgress(serial);
        const p = prog.data;
        if (p && p.dir === direction) {
          const now = Date.now();
          const deltaBytes = p.done - lastDone;
          const deltaMs = now - lastTs;
          if (lastDone > 0 && deltaMs > 0) {
            const mbps = Math.max(0, (deltaBytes * 8) / (deltaMs / 1000) / 1e6);
            setLiveMbps(mbps);
            setLiveHistory(h => {
              const time = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
              const next = [...h, { time, mbps }];
              return next.length > 40 ? next.slice(-40) : next;
            });
          }
          lastDone = p.done; lastTs = now;
        }
      } catch { /* progress polling is best-effort */ }

      const { data } = await acsAPI.getSpeedtestResults(serial);
      const fresh = data.data?.[direction];
      const state = fresh?.state;
      const isFreshResult = JSON.stringify(fresh || null) !== prevFingerprint;
      if (isFreshResult) setSpeedtest(data.data);
      if (isFreshResult && state && state !== 'Requested' && state !== 'None') {
        if (state !== 'Completed') throw new Error(`${direction === 'download' ? 'Download' : 'Upload'} test: ${state}`);
        return;
      }
    }
    throw new Error('Speed test is taking longer than expected');
  };

  const runSpeedtest = async () => {
    try {
      await runOneDirection('download');
      await runOneDirection('upload');
      toast.success('Speed test complete');
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Speed test failed');
    } finally {
      setStRunning(null);
      setLiveMbps(null);
    }
  };

  const blockedMacs = new Set(
    aclInfo?.supported ? aclInfo.networks.flatMap(n => n.entries.map(e => e.mac.toLowerCase())) : []
  );

  const blockDevice = async (mac) => {
    if (!window.confirm(`Block ${mac} from this router's WiFi? Its network access will be cut immediately (if online) or on next reconnect.`)) return;
    setAclBusy(mac);
    try {
      const { data } = await acsAPI.blockDevice(serial, mac);
      toast.success(data.data?.completed ? 'Device blocked' : 'Block queued — applies when the ONT next connects');
      await loadAcl();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Block failed');
    } finally { setAclBusy(null); }
  };

  const unblockDevice = async (mac) => {
    setAclBusy(mac);
    try {
      const { data } = await acsAPI.unblockDevice(serial, mac);
      toast.success(data.data?.completed ? 'Device unblocked' : 'Unblock queued — applies when the ONT next connects');
      await loadAcl();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Unblock failed');
    } finally { setAclBusy(null); }
  };

  const toggleRawParams = async () => {
    if (rawParams) { setRawParams(null); return; }
    setRawLoading(true);
    try {
      const { data } = await acsAPI.getDevice(serial, { raw: 1 });
      setRawParams(data.data.parameters || {});
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load parameters');
    } finally { setRawLoading(false); }
  };

  const runAction = async (name, fn, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(name);
    try {
      await fn();
      toast.success(`${name} sent`);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.message || `${name} failed`);
    } finally { setBusy(null); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
        <div className="modal-header">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Router size={16} color="var(--primary)" /> {serial}
          </h3>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {loading || !device ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
              <div className="spinner" style={{ margin: '0 auto 12px' }} />Loading…
            </div>
          ) : (
            <>
              <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: '.85rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{device.model || device.productClass || 'Unknown model'}</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '.78rem' }}>{device.manufacturer} · SW {device.swVersion || '—'}</div>
                  </div>
                  <span className={`badge badge-${device.online ? 'active' : 'danger'}`}>{device.online ? 'Online' : 'Offline'}</span>
                </div>
                {device.customer && !relinking ? (
                  <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                    <span>{device.customer.name} ({device.customer.customerId}) {device.customer.area ? `— ${device.customer.area}` : ''}</span>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => setRelinking(true)}>Relink</button>
                      <button
                        className="btn btn-secondary btn-sm"
                        disabled={unlinking}
                        onClick={async () => {
                          if (!window.confirm(`Unlink this ONT from ${device.customer.name}?`)) return;
                          setUnlinking(true);
                          try {
                            await acsAPI.unlinkCustomer(serial);
                            toast.success('Unlinked');
                            await load();
                            onChanged?.();
                          } catch (err) {
                            toast.error(err.response?.data?.message || 'Unlink failed');
                          } finally { setUnlinking(false); }
                        }}
                      >
                        {unlinking ? 'Unlinking…' : 'Unlink'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <CustomerLinkBox
                    serial={serial}
                    pppoeUsername={device.pppoeUsername}
                    currentCustomer={relinking ? device.customer : null}
                    onLinked={async () => { setRelinking(false); await load(); onChanged?.(); }}
                    onCancel={relinking ? () => setRelinking(false) : undefined}
                  />
                )}
                <div style={{ marginTop: 8, display: 'flex', gap: 16, flexWrap: 'wrap', color: 'var(--text-muted)', fontSize: '.78rem' }}>
                  <span>Last seen: {timeAgo(device.lastInform)}</span>
                  <span>IP: {device.ip || device.remoteIp || '—'}</span>
                  {device.optical && (device.optical.rx != null) && <span>Rx: {device.optical.rx} dBm</span>}
                  {device.optical && (device.optical.tx != null) && <span>Tx: {device.optical.tx} dBm</span>}
                </div>
              </div>

              {/* WiFi networks */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 600, fontSize: '.85rem', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Radio size={14} /> WiFi Networks
                </div>
                {(device.wifiFull || []).length === 0 ? (
                  <div style={{ color: 'var(--text-muted)', fontSize: '.82rem' }}>No WiFi data yet</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {device.wifiFull.map(w => (
                      <div key={w.instance} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.82rem', background: 'var(--bg)', borderRadius: 6, padding: '6px 10px' }}>
                        <span>{w.ssid || <em style={{ color: 'var(--text-muted)' }}>(no SSID)</em>} <span style={{ color: 'var(--text-muted)' }}>· {w.band}GHz</span></span>
                        <span style={{ color: 'var(--text-muted)' }}>{w.enabled ? `${w.clients} client(s)` : 'disabled'}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* WAN / Internet connections */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 600, fontSize: '.85rem', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Globe size={14} /> WAN / Internet
                </div>
                {(device.wanFull || []).length === 0 ? (
                  <div style={{ color: 'var(--text-muted)', fontSize: '.82rem' }}>No WAN data yet</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {device.wanFull.map(w => (
                      <div key={w.path} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.82rem', background: 'var(--bg)', borderRadius: 6, padding: '6px 10px' }}>
                        <span>{w.username || <em style={{ color: 'var(--text-muted)' }}>(no username)</em>} <span style={{ color: 'var(--text-muted)' }}>· {w.type}{w.vlan ? ` · VLAN ${w.vlan}` : ''}</span></span>
                        <span style={{ color: 'var(--text-muted)' }}>{w.connectionStatus || '—'}{w.externalIp ? ` · ${w.externalIp}` : ''}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Connected hosts */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 600, fontSize: '.85rem', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Users size={14} /> Connected Devices ({(device.hosts || []).filter(h => h.active).length})
                </div>
                {(device.hosts || []).filter(h => h.active).length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {device.hosts.filter(h => h.active).slice(0, 10).map((h, i) => {
                      const isBlocked = h.mac && blockedMacs.has(h.mac.toLowerCase());
                      return (
                        <div key={i} style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                          <span>{h.hostname || h.mac}</span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span>{h.ip} · {h.interface}</span>
                            {aclInfo?.supported && h.mac && !isBlocked && (
                              <button
                                className="btn btn-danger btn-sm"
                                style={{ padding: '2px 8px', fontSize: '.7rem' }}
                                disabled={aclBusy === h.mac}
                                onClick={() => blockDevice(h.mac)}
                              >
                                <Ban size={10} style={{ marginRight: 3 }} />{aclBusy === h.mac ? 'Blocking…' : 'Block'}
                              </button>
                            )}
                            {isBlocked && <span className="badge badge-danger" style={{ fontSize: '.65rem' }}>Blocked</span>}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
                {aclInfo && !aclInfo.supported && (
                  <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 6, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <ShieldOff size={12} />{aclInfo.reason || 'Device blocking not supported on this router model'}
                  </div>
                )}
              </div>

              {/* Blocked devices */}
              {aclInfo?.supported && aclInfo.networks.some(n => n.entries.length > 0) && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, fontSize: '.85rem', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <ShieldCheck size={14} /> Blocked Devices
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {aclInfo.networks.flatMap(n => n.entries.map(e => ({ ...e, band: n.band, ssid: n.ssid }))).map((e, i) => (
                      <div key={i} style={{ fontSize: '.78rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg)', borderRadius: 6, padding: '5px 10px' }}>
                        <span>{e.mac} <span style={{ color: 'var(--text-muted)' }}>· {e.ssid || `${e.band}GHz`}</span></span>
                        <button
                          className="btn btn-secondary btn-sm"
                          style={{ padding: '2px 8px', fontSize: '.7rem' }}
                          disabled={aclBusy === e.mac}
                          onClick={() => unblockDevice(e.mac)}
                        >
                          {aclBusy === e.mac ? 'Unblocking…' : 'Unblock'}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Speed Test (TR-143) */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 600, fontSize: '.85rem', marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Gauge size={14} /> Speed Test</span>
                  <button className="btn btn-primary btn-sm" disabled={!!stRunning} onClick={runSpeedtest}>
                    <Gauge size={12} style={{ marginRight: 5 }} />
                    {stRunning ? `Testing ${stRunning === 'download' ? 'Download' : 'Upload'}…` : 'Test'}
                  </button>
                </div>

                {stRunning ? (
                  <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '10px 16px', background: 'var(--bg)',
                      borderBottom: liveHistory.length > 1 ? '1px solid var(--border)' : 'none',
                    }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontWeight: 600, fontSize: '.82rem' }}>
                        <span style={{
                          width: 7, height: 7, borderRadius: '50%',
                          background: stRunning === 'download' ? 'var(--primary)' : 'var(--cb-ea580c)',
                          display: 'inline-block', animation: 'livePulse 1.8s ease-out infinite',
                        }} />
                        {stRunning === 'download' ? '↓ Download' : '↑ Upload'}
                      </span>
                      <span style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>live</span>
                    </div>
                    <div style={{ padding: '16px 20px', textAlign: 'center' }}>
                      <span style={{
                        fontSize: '2.4rem', fontWeight: 800, lineHeight: 1, fontVariantNumeric: 'tabular-nums',
                        color: stRunning === 'download' ? 'var(--primary)' : 'var(--c-ea580c)',
                      }}>
                        {liveMbps != null ? liveMbps.toFixed(1) : '—'}
                      </span>
                      <span style={{ fontSize: '.9rem', color: 'var(--text-muted)', marginLeft: 6 }}>Mbps</span>
                    </div>
                    {liveHistory.length > 1 && (
                      <div style={{ borderTop: '1px solid var(--border)', padding: '8px 8px 4px', height: 110 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={liveHistory} margin={{ top: 4, right: 10, left: 0, bottom: 0 }}>
                            <defs>
                              <linearGradient id="stLive" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={stRunning === 'download' ? 'var(--primary)' : 'var(--cl-ea580c)'} stopOpacity={0.25} />
                                <stop offset="95%" stopColor={stRunning === 'download' ? 'var(--primary)' : 'var(--cl-ea580c)'} stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <XAxis dataKey="time" tick={{ fontSize: 9, fill: 'var(--text-muted)' }} axisLine={{ stroke: 'var(--border)' }} tickLine={false} minTickGap={40} interval="preserveStartEnd" />
                            <YAxis hide domain={[0, 'auto']} />
                            <Tooltip contentStyle={{ fontSize: '.75rem', borderRadius: 6, padding: '4px 8px' }} formatter={(v) => [`${v.toFixed(1)} Mbps`, stRunning === 'download' ? '↓ Down' : '↑ Up']} />
                            <Area type="monotone" dataKey="mbps" stroke={stRunning === 'download' ? 'var(--primary)' : 'var(--cl-ea580c)'} strokeWidth={2} fill="url(#stLive)" dot={false} isAnimationActive={false} />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    {['download', 'upload'].map(dir => {
                      const r = speedtest?.[dir];
                      return (
                        <div key={dir} style={{ flex: 1, background: 'var(--bg)', borderRadius: 6, padding: '8px 10px' }}>
                          <div style={{ fontSize: '.68rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.03em', marginBottom: 2 }}>
                            {dir === 'download' ? 'Download' : 'Upload'}
                          </div>
                          <div style={{ fontSize: '1rem', fontWeight: 700 }}>
                            {r?.mbps != null ? `${r.mbps.toFixed(2)} Mbps` : '—'}
                          </div>
                          <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>{r?.state || 'None'}</div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div style={{ fontSize: '.7rem', color: 'var(--text-muted)', marginTop: 6 }}>
                  Runs on the router itself against this ACS — measures the customer's actual line, no site visit needed. Takes up to ~90s.
                </div>
                {SPEEDTEST_VENDOR_NOTES[device.profile] && (
                  <div style={{
                    fontSize: '.72rem', color: 'var(--c-92400e)', background: 'var(--cb-fffbeb)',
                    border: '1px solid var(--cb-fde68a)', borderRadius: 6, padding: '6px 10px', marginTop: 6,
                    display: 'flex', alignItems: 'flex-start', gap: 6,
                  }}>
                    <AlertTriangle size={12} style={{ flexShrink: 0, marginTop: 2 }} />
                    {SPEEDTEST_VENDOR_NOTES[device.profile]}
                  </div>
                )}
              </div>

              {/* Recent tasks */}
              {(device.tasks || []).length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, fontSize: '.85rem', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Activity size={14} /> Recent Tasks
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {device.tasks.slice(0, 8).map(t => (
                      <div key={t.id} style={{ fontSize: '.78rem', display: 'flex', justifyContent: 'space-between', color: 'var(--text-muted)' }}>
                        <span>{t.label}</span>
                        <span className={`badge badge-${t.status === 'done' ? 'active' : t.status === 'fault' ? 'danger' : 'warning'}`} style={{ fontSize: '.68rem' }}>
                          {t.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* TR-069 logs */}
              {(device.logs || []).length > 0 && (
                <div style={{ marginBottom: 16, border: '1px solid var(--border)', borderRadius: 8, padding: 12, background: 'var(--card-bg, #fff)' }}>
                  <div style={{ fontWeight: 600, fontSize: '.85rem', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <List size={14} /> TR-069 Logs
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 220, overflowY: 'auto', background: 'var(--bg)', borderRadius: 6, padding: 8 }}>
                    {device.logs.map((l, i) => (
                      <div key={i} style={{ fontSize: '.76rem', display: 'flex', gap: 8, color: 'var(--text-muted)' }}>
                        <span style={{ flexShrink: 0, width: 62, color: 'var(--text-muted)', opacity: .8 }}>{timeAgo(l.ts)}</span>
                        <span className={`badge badge-${l.kind === 'fault' ? 'danger' : l.kind === 'task' ? 'active' : 'warning'}`} style={{ fontSize: '.65rem', flexShrink: 0, alignSelf: 'flex-start' }}>
                          {l.kind}
                        </span>
                        <span style={{ wordBreak: 'break-word' }}>{l.msg}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Technical details */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 600, fontSize: '.85rem', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Settings size={14} /> Technical Details
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
                  {[
                    ['Serial', device.serial],
                    ['Manufacturer', device.manufacturer || '—'],
                    ['Product Class', device.productClass || '—'],
                    ['Hardware Version', device.hwVersion || '—'],
                    ['Software Version', device.swVersion || '—'],
                    ['Profile', device.profile || '—'],
                    ['Uptime', fmtDuration(device.upTime)],
                    ['Inform Interval', device.informInterval ? `${device.informInterval}s` : '—'],
                    ['Inform Count', device.informCount ?? '—'],
                    ['Registered', device.registeredAt ? new Date(device.registeredAt).toLocaleString() : '—'],
                    ['Remote IP', device.remoteIp || '—'],
                    ['Connection Request URL', device.connReqUrl || '—'],
                  ].map(([label, val]) => (
                    <div key={label} style={{ background: 'var(--bg)', borderRadius: 6, padding: '8px 10px' }}>
                      <div style={{ fontSize: '.68rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.03em', marginBottom: 2 }}>{label}</div>
                      <div style={{ fontSize: '.78rem', fontWeight: 600, wordBreak: 'break-all' }}>{val}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Raw TR-069 parameters */}
              <div>
                <button className="btn btn-secondary btn-sm" onClick={toggleRawParams} disabled={rawLoading}>
                  <List size={12} style={{ marginRight: 5 }} />
                  {rawLoading ? 'Loading…' : rawParams ? 'Hide Raw Parameters' : 'Show Raw Parameters'}
                </button>
                {rawParams && (
                  <div style={{ marginTop: 10, maxHeight: 260, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 6 }}>
                    <table style={{ width: '100%', fontSize: '.72rem' }}>
                      <tbody>
                        {Object.entries(rawParams).sort(([a], [b]) => a.localeCompare(b)).map(([k, v]) => (
                          <tr key={k} style={{ borderBottom: '1px solid var(--border)' }}>
                            <td style={{ padding: '4px 8px', color: 'var(--text-muted)', whiteSpace: 'nowrap', verticalAlign: 'top' }}>{k}</td>
                            <td style={{ padding: '4px 8px', wordBreak: 'break-all' }}>{String(v)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        {!loading && device && (
          <div style={{ padding: '0 24px 20px', display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => runAction('Refresh', () => acsAPI.refresh(serial))}>
              <RefreshCw size={12} style={{ marginRight: 5 }} />{busy === 'Refresh' ? 'Refreshing…' : 'Refresh'}
            </button>
            <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => setShowWifi(true)}>
              <Wifi size={12} style={{ marginRight: 5 }} />WiFi
            </button>
            <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => setShowWan(true)}>
              <Globe size={12} style={{ marginRight: 5 }} />WAN
            </button>
            <button className="btn btn-warning btn-sm" disabled={busy} onClick={() => runAction('Reboot', () => acsAPI.reboot(serial), `Reboot ${serial}? Internet will drop for 2-3 minutes.`)}>
              <RotateCcw size={12} style={{ marginRight: 5 }} />{busy === 'Reboot' ? 'Rebooting…' : 'Reboot'}
            </button>
            <button className="btn btn-danger btn-sm" disabled={busy} onClick={() => runAction('Factory Reset', () => acsAPI.factoryReset(serial), `Factory reset ${serial}? This wipes all its settings and cannot be undone.`)}>
              <AlertTriangle size={12} style={{ marginRight: 5 }} />Factory Reset
            </button>
          </div>
        )}
      </div>
      {showWifi && (
        <WifiModal serial={serial} wifi={device?.wifiFull} onClose={() => setShowWifi(false)} onSuccess={load} />
      )}
      {showWan && (
        <WanModal serial={serial} wan={device?.wanFull} onClose={() => setShowWan(false)} onSuccess={load} />
      )}
    </div>
  );
}

// ─── Pending Tasks Modal ──────────────────────────────────────────────────────
function PendingTasksModal({ onClose, onSelectDevice }) {
  const [rows, setRows] = useState(null); // null = loading

  useEffect(() => {
    let cancelled = false;
    acsAPI.getPendingTasks()
      .then(({ data }) => { if (!cancelled) setRows(data.data); })
      .catch((err) => { if (!cancelled) { toast.error(err.response?.data?.message || 'Failed to load pending tasks'); setRows([]); } });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
        <div className="modal-header">
          <h3><Activity size={16} style={{ marginRight: 6, verticalAlign: -2 }} />Pending Tasks</h3>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          {rows === null ? (
            <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
          ) : rows.length === 0 ? (
            <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)' }}>No pending tasks — queue is clear.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 420, overflowY: 'auto' }}>
              {rows.map(t => (
                <div key={t.id} onClick={() => onSelectDevice(t.serial)}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 6, cursor: 'pointer', border: '1px solid var(--border)' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '.82rem', fontWeight: 600 }}>{t.label || t.type}</div>
                    <div style={{ fontSize: '.74rem', color: 'var(--text-muted)' }}>{t.serial}{t.model ? ` · ${t.model}` : ''}</div>
                  </div>
                  <span className={`badge badge-${t.status === 'sent' ? 'active' : 'warning'}`} style={{ fontSize: '.68rem', flexShrink: 0 }}>{t.status}</span>
                  <span style={{ fontSize: '.72rem', color: 'var(--text-muted)', flexShrink: 0, width: 60, textAlign: 'right' }}>{timeAgo(t.createdAt)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main ACS Page ────────────────────────────────────────────────────────────
export default function AcsPage() {
  useDarkReady();
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState(null); // null = checking
  const [connInfo, setConnInfo] = useState(null);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [onlineFilter, setOnlineFilter] = useState('');
  const [selected, setSelected] = useState(() => searchParams.get('serial') || null);
  const [selectedRelink, setSelectedRelink] = useState(false);
  const [wifiTarget, setWifiTarget] = useState(null);
  const [wanTarget, setWanTarget] = useState(null);
  const [showPendingTasks, setShowPendingTasks] = useState(false);
  const [autoLinking, setAutoLinking] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await acsAPI.getStatus();
      setStatus(data.data);
      if (data.data.connected) {
        try {
          const res = await acsAPI.getConnectionInfo();
          setConnInfo(res.data.data);
        } catch { /* connection-info is a nice-to-have, don't block the page on it */ }
      }
    } catch (err) {
      setStatus({ connected: false });
    }
  }, []);

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await acsAPI.getDevices({ q: search || undefined, online: onlineFilter || undefined, limit: 100 });
      setDevices(data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load ONTs');
    } finally { setLoading(false); }
  }, [search, onlineFilter]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);
  useEffect(() => {
    const t = setTimeout(() => { if (status?.connected) fetchDevices(); }, 300);
    return () => clearTimeout(t);
  }, [status?.connected, search, onlineFilter, fetchDevices]);

  const handleDelete = async (serial) => {
    if (!window.confirm(`Remove ${serial} from the ACS? (It will re-register automatically if still connected.)`)) return;
    try {
      await acsAPI.deleteDevice(serial);
      toast.success('Removed');
      fetchDevices();
    } catch (err) { toast.error(err.response?.data?.message || 'Delete failed'); }
  };

  // Bulk version of the per-row "link by WAN username" action — walks every unlinked
  // ONT that has a WAN/PPPoE username on file and tries to match it to a customer.
  const handleAutoLinkAll = async () => {
    const candidates = devices.filter(d => !d.customer && d.pppoeUsername);
    if (!candidates.length) { toast.error('No unlinked ONTs with a WAN username to match'); return; }
    setAutoLinking(true);
    let linked = 0;
    try {
      for (const d of candidates) {
        try {
          await acsAPI.autoLinkCustomer(d.serial);
          linked++;
        } catch { /* no match for this one — keep going */ }
      }
      toast.success(`Linked ${linked} of ${candidates.length} ONT${candidates.length === 1 ? '' : 's'} via WAN username`);
      fetchDevices();
    } finally { setAutoLinking(false); }
  };

  if (status === null) {
    return (
      <div className="card" style={{ maxWidth: 400, margin: '40px auto' }}>
        <div className="card-body" style={{ textAlign: 'center', padding: 40 }}>
          <div className="spinner" style={{ margin: '0 auto 16px' }} />
          <p style={{ color: 'var(--text-muted)' }}>Connecting to ACS…</p>
        </div>
      </div>
    );
  }

  if (!status.connected) {
    return (
      <div className="card" style={{ maxWidth: 500, margin: '40px auto' }}>
        <div className="card-body" style={{ textAlign: 'center', padding: 40 }}>
          <Router size={48} style={{ color: 'var(--text-muted)', marginBottom: 16 }} />
          <h3 style={{ marginBottom: 8 }}>ACS Server Not Reachable</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: '.9rem', lineHeight: 1.6 }}>
            The TR-069 management service isn't responding. Check that the <code>angel-isp-acs</code> process is running.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {connInfo && <ConnectionInfoPanel info={connInfo} />}

      <div className="stats-grid" style={{ marginBottom: 24 }}>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'var(--cb-dbeafe)' }}><Router size={20} style={{ color: 'var(--c-1e40af)' }} /></div>
          <div className="stat-info">
            <p>Total ONTs</p>
            <h3>{status.total ?? 0}</h3>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'var(--cb-d1fae5)' }}><Wifi size={20} style={{ color: 'var(--c-065f46)' }} /></div>
          <div className="stat-info">
            <p>Online</p>
            <h3 style={{ color: 'var(--success)' }}>{status.online ?? 0}</h3>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'var(--cb-fee2e2)' }}><WifiOff size={20} style={{ color: 'var(--c-991b1b)' }} /></div>
          <div className="stat-info">
            <p>Offline</p>
            <h3 style={{ color: 'var(--danger)' }}>{status.offline ?? 0}</h3>
          </div>
        </div>
        <div className="stat-card" style={{ cursor: 'pointer' }} onClick={() => setShowPendingTasks(true)}>
          <div className="stat-icon" style={{ background: 'var(--cb-fef3c7)' }}><Activity size={20} style={{ color: 'var(--c-92400e)' }} /></div>
          <div className="stat-info">
            <p>Pending Tasks</p>
            <h3>{status.pendingTasks ?? 0}</h3>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', flex: 1 }}>
            <div style={{ position: 'relative', flex: 1, minWidth: 200, maxWidth: 340 }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
              <input
                className="form-control"
                style={{ paddingLeft: 32, height: 34 }}
                placeholder="Search serial, model, IP…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <select className="form-control" style={{ height: 34, width: 'auto', minWidth: 140 }} value={onlineFilter} onChange={e => setOnlineFilter(e.target.value)}>
              <option value="">All ONTs</option>
              <option value="true">Online only</option>
              <option value="false">Offline only</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary btn-sm" disabled={autoLinking} title="Match unlinked ONTs to customers by WAN/PPPoE username" onClick={handleAutoLinkAll}>
              <Link2 size={13} style={{ marginRight: 5 }} />{autoLinking ? 'Linking…' : 'Autolink via WAN'}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => { fetchStatus(); fetchDevices(); }}>
              <RefreshCw size={13} style={{ marginRight: 5 }} />Refresh
            </button>
          </div>
        </div>

        <div className="table-container">
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
              <div className="spinner" style={{ margin: '0 auto 12px' }} />Loading ONTs…
            </div>
          ) : devices.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
              <Router size={32} style={{ marginBottom: 8 }} />
              <div>No ONTs have registered with the ACS yet</div>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Serial / Model</th>
                  <th>Customer</th>
                  <th>Status</th>
                  <th>WiFi</th>
                  <th>WAN</th>
                  <th>Last Seen</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {devices.map(d => (
                  <tr key={d.id} style={{ cursor: 'pointer' }} onClick={() => setSelected(d.serial)}>
                    <td>
                      <div className="font-mono" style={{ fontSize: '.82rem', fontWeight: 600 }}>{d.serial}</div>
                      <div style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>{d.model}</div>
                    </td>
                    <td style={{ fontSize: '.82rem' }} onClick={e => d.customer ? undefined : e.stopPropagation()}>
                      {d.customer ? (
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                          <div>
                            <div>{d.customer.name}</div>
                            <div style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>{d.customer.customerId}</div>
                          </div>
                          <button
                            className="btn btn-secondary btn-sm"
                            title="Wrong customer? Relink this ONT"
                            style={{ padding: '2px 6px', flexShrink: 0 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedRelink(true);
                              setSelected(d.serial);
                            }}
                          >
                            <Edit2 size={11} />
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ color: 'var(--text-muted)' }}>Not linked</span>
                          {d.pppoeUsername && (
                            <button
                              className="btn btn-secondary btn-sm"
                              title={`Link to customer with WAN username "${d.pppoeUsername}"`}
                              style={{ padding: '2px 6px' }}
                              onClick={async () => {
                                try {
                                  const { data } = await acsAPI.autoLinkCustomer(d.serial);
                                  toast.success(`Linked to ${data.data.customer.name} (${data.data.customer.customerId})`);
                                  fetchDevices();
                                } catch (err) {
                                  toast.error(err.response?.data?.message || 'No match — open the ONT to link manually');
                                }
                              }}
                            >
                              <Link2 size={11} />
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                    <td>
                      <span className={`badge badge-${d.online ? 'active' : 'danger'}`}>{d.online ? 'Online' : 'Offline'}</span>
                    </td>
                    <td style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>
                      {(d.wifi || []).filter(w => w.enabled).map(w => w.ssid).join(', ') || '—'}
                    </td>
                    <td style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>
                      {d.pppoeUsername || '—'}
                    </td>
                    <td style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>{timeAgo(d.lastInform)}</td>
                    <td onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn btn-secondary btn-sm" title="Change WiFi name/password" style={{ padding: '3px 7px' }} onClick={() => setWifiTarget(d.serial)}>
                          <Wifi size={12} />
                        </button>
                        <button className="btn btn-secondary btn-sm" title="Change WAN/PPPoE details" style={{ padding: '3px 7px' }} onClick={() => setWanTarget(d.serial)}>
                          <Globe size={12} />
                        </button>
                        <button className="btn btn-secondary btn-sm" title="Reboot" style={{ padding: '3px 7px' }}
                          onClick={async () => {
                            if (!window.confirm(`Reboot ${d.serial}?`)) return;
                            try { await acsAPI.reboot(d.serial); toast.success('Reboot sent'); } catch (err) { toast.error(err.response?.data?.message || 'Reboot failed'); }
                          }}>
                          <Power size={12} />
                        </button>
                        <button className="btn btn-danger btn-sm" title="Remove from ACS" style={{ padding: '3px 7px' }} onClick={() => handleDelete(d.serial)}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {selected && (
        <DeviceDetailModal
          serial={selected}
          initialRelink={selectedRelink}
          onClose={() => { setSelected(null); setSelectedRelink(false); if (searchParams.get('serial')) setSearchParams({}); }}
          onChanged={fetchDevices}
        />
      )}
      {showPendingTasks && (
        <PendingTasksModal
          onClose={() => setShowPendingTasks(false)}
          onSelectDevice={(serial) => { setShowPendingTasks(false); setSelected(serial); }}
        />
      )}
      {wifiTarget && (
        <WifiModal
          serial={wifiTarget}
          wifi={devices.find(d => d.serial === wifiTarget)?.wifi}
          onClose={() => setWifiTarget(null)}
          onSuccess={fetchDevices}
        />
      )}
      {wanTarget && (
        <WanModal
          serial={wanTarget}
          wan={devices.find(d => d.serial === wanTarget)?.wan}
          onClose={() => setWanTarget(null)}
          onSuccess={fetchDevices}
        />
      )}
    </div>
  );
}
